<template>
  <section class="content">
    <div class="account-element flex">
      <div class="left-content">
        <div class="left-header">
          <span>{{ $t('核算维度') }}</span>
          <a-icon type="plus" class="add-icon" @click="handleAddDimension" />
        </div>
        <div class="dimension-list">
          <div
            v-for="item in dimensionList"
            :key="item.id"
            class="dimension-item"
            :class="{ active: selectedDimension && selectedDimension.item_type === item.item_type }"
            @click="handleSelectDimension(item)"
          >
            <div class="item-info">
              <div class="item-name">{{ item.name }}</div>
              <div class="item-code">{{ item.code }}</div>
            </div>
            <a-icon type="edit" class="edit-icon" @click.stop="handleEditDimension(item)" />
          </div>
        </div>
      </div>
      <div class="right-content">
        <div class="form-content">
          <div style="margin-top: 15px; justify-content: space-between" class="flex-ct">
            <div class="flex-ct" style="gap: 10px">
              <span class="selected-title" v-if="selectedDimension">
                {{ selectedDimension.name }} {{ $t('明细') }}
              </span>
              <a-button
                v-if="selectedDimension"
                @click="handleAddDimensionItem"
                type="primary"
                style="margin-right: 10px"
              >
                {{ $t('新增维度明细') }}
              </a-button>
            </div>
            <div style="display: flex; align-items: center; justify-content: flex-end">
              <a-input
                v-if="selectedDimension"
                v-model="nameOrCode"
                allowClear
                style="width: 300px; margin-right: 10px"
                :placeholder="$t('请输入名称或编码')"
                @pressEnter="searchHandle"
                @change="onKeywordChange"
              >
                <a-icon slot="prefix" type="search" />
              </a-input>
              <Pagination
                style="width: auto; min-width: 350px"
                v-if="total > 0"
                :pageNo="pageNo"
                :length="pageSize"
                :total-count="total"
                @paginate="paginate"
              />
            </div>
          </div>
        </div>
        <div class="table-content">
          <div class="table-body">
            <CommonTable
              :columns="columns"
              :data-source="itemList"
              :scroll="{ y: scrollHeight }"
              :loading="loading"
              :showPagination="false"
              rowKey="id"
            >
              <template v-slot:operation="{ record }">
                <a @click="handleEditDimensionItem(record)">{{ $t('编辑') }}</a>
              </template>
            </CommonTable>
          </div>
        </div>
      </div>
    </div>

    <!-- 核算维度弹窗 -->
    <a-modal
      :title="dimensionModalTitle"
      :visible="dimensionVisible"
      @ok="handleDimensionOk"
      @cancel="dimensionVisible = false"
      :confirmLoading="confirmLoading"
      destroyOnClose
    >
      <a-form-model
        ref="dimensionForm"
        :model="dimensionForm"
        :rules="dimensionRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item :label="$t('维度名称')" prop="name">
          <a-input v-model="dimensionForm.name" :placeholder="$t('请输入维度名称')" />
        </a-form-model-item>
        <a-form-model-item :label="$t('维度编码')" prop="code">
          <a-input
            v-model="dimensionForm.code"
            :placeholder="$t('请输入维度编码')"
            :disabled="!!dimensionForm.id"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('项目类型')" prop="itemType">
          <a-input
            v-model="dimensionForm.itemType"
            :placeholder="$t('请输入项目类型(如 customer)')"
            :disabled="!!dimensionForm.id"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('类型名称')" prop="itemTypeName">
          <a-input
            v-model="dimensionForm.itemTypeName"
            :placeholder="$t('请输入类型名称(如 客户)')"
            :disabled="!!dimensionForm.id"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('取值编码')" prop="valueCode">
          <a-input
            v-model="dimensionForm.valueCode"
            :placeholder="$t('请输入取值编码(如 customerName)')"
            :disabled="!!dimensionForm.id"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('取值名称')" prop="valueName">
          <a-input
            v-model="dimensionForm.valueName"
            :placeholder="$t('请输入取值名称(如 客户名称)')"
            :disabled="!!dimensionForm.id"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 维度明细弹窗 -->
    <a-modal
      :title="itemModalTitle"
      :visible="itemVisible"
      @ok="handleItemOk"
      @cancel="itemVisible = false"
      :confirmLoading="confirmLoading"
      destroyOnClose
    >
      <a-form-model
        ref="itemForm"
        :model="itemForm"
        :rules="itemRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item :label="$t('明细名称')" prop="name">
          <a-input v-model="itemForm.name" :placeholder="$t('请输入明细名称')" />
        </a-form-model-item>
        <a-form-model-item :label="$t('明细编码')" prop="code">
          <a-input v-model="itemForm.code" :placeholder="$t('请输入明细编码')" />
        </a-form-model-item>
        <a-form-model-item :label="$t(`结算组织`)" required>
          <CommonSelect
            :isBu="true"
            :list="$store.state.settlementBuList"
            :code.sync="itemForm.business_unit_code"
            :name.sync="itemForm.business_unit_name"
          />
        </a-form-model-item>
        <a-form-model-item :label="$t('维度名称')" v-if="selectedDimension">
          <a-input :value="selectedDimension.name" disabled />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </section>
</template>

<script>
import debounce from '@/common/debounce'
import http from '@/service/axios'
import api from '@/service/api'
import Pagination from '@/component/pagination'

export default {
  name: 'DimensionList',
  components: { Pagination },
  data() {
    return {
      loading: false,
      confirmLoading: false,
      dimensionList: [],
      selectedDimension: null,
      itemList: [],
      pageNo: 1,
      pageSize: 20,
      total: 0,
      // 维度明细筛选：名称或编码
      nameOrCode: '',

      // Dimension Modal
      dimensionVisible: false,
      dimensionForm: {
        id: undefined,
        name: '',
        code: '',
        itemType: '',
        itemTypeName: '',
        valueCode: '',
        valueName: '',
      },
      dimensionRules: {
        name: [{ required: true, message: this.$t('请输入维度名称'), trigger: 'blur' }],
        code: [{ required: true, message: this.$t('请输入维度编码'), trigger: 'blur' }],
        itemType: [{ required: true, message: this.$t('请输入项目类型'), trigger: 'blur' }],
        itemTypeName: [{ required: true, message: this.$t('请输入类型名称'), trigger: 'blur' }],
        valueCode: [{ required: true, message: this.$t('请输入取值编码'), trigger: 'blur' }],
        valueName: [{ required: true, message: this.$t('请输入取值名称'), trigger: 'blur' }],
      },

      // Item Modal
      itemVisible: false,
      itemForm: {
        id: undefined,
        name: undefined,
        code: undefined,
        business_unit_name: undefined,
        business_unit_code: undefined,
      },
      itemRules: {
        name: [{ required: true, message: this.$t('请输入明细名称'), trigger: 'blur' }],
        code: [{ required: true, message: this.$t('请输入明细编码'), trigger: 'blur' }],
      },
    }
  },
  computed: {
    scrollHeight() {
      return window.innerHeight - 280
    },
    dimensionModalTitle() {
      return this.dimensionForm.id ? this.$t('编辑核算维度') : this.$t('新增核算维度')
    },
    itemModalTitle() {
      return this.itemForm.id ? this.$t('编辑维度明细') : this.$t('新增维度明细')
    },
    columns() {
      return [
        {
          title: this.$t('编码'),
          dataIndex: 'code',
          width: 150,
        },
        {
          title: this.$t('名称'),
          dataIndex: 'name',
        },
        {
          title: this.$t('结算组织'),
          dataIndex: 'business_unit_name',
        },
        {
          title: this.$t('操作'),
          dataIndex: 'operation',
          scopedSlots: { customRender: 'operation' },
          width: 100,
        },
      ]
    },
  },
  created() {
    this.debounceSearch = debounce(this.searchHandle, 400)
  },
  mounted() {
    this.getDimensionList()
  },
  methods: {
    getDimensionList() {
      http({
        url: api.getAccountDimensionList,
        type: 'post',
        data: {
          page_no: 1,
          page_size: 1000,
        },
        success: (res) => {
          this.dimensionList = res.result?.list || []
          if (!this.selectedDimension && this.dimensionList.length > 0) {
            this.handleSelectDimension(this.dimensionList[0])
          }
        },
      })
    },
    handleSelectDimension(item) {
      // 切换维度时清掉上一个维度的筛选条件
      if (this.selectedDimension && this.selectedDimension.item_type !== item.item_type) {
        this.nameOrCode = ''
      }
      this.selectedDimension = item
      this.pageNo = 1
      this.getItemList()
    },
    searchHandle() {
      if (!this.selectedDimension) return
      this.pageNo = 1
      this.getItemList()
    },
    onKeywordChange() {
      this.debounceSearch()
    },
    getItemList() {
      if (!this.selectedDimension) return
      const data = {
        page_no: this.pageNo,
        page_size: this.pageSize,
        type: this.selectedDimension.item_type,
      }
      if (this.nameOrCode) {
        data.name_or_code = this.nameOrCode
      }
      this.loading = true
      http({
        url: api.getAccountDimensionItemList,
        type: 'post',
        data,
        success: (res) => {
          this.loading = false
          this.itemList = res.result?.list || []
          this.total = res.result?.total || 0
        },
        fail: () => {
          this.loading = false
        },
      })
    },
    paginate(data) {
      this.pageNo = data.pageNo
      this.pageSize = data.pageSize
      this.getItemList()
    },

    // Dimension Management
    handleAddDimension() {
      this.dimensionForm = {
        id: undefined,
        code: '',
        name: '',
        itemType: '',
        itemTypeName: '',
        valueCode: '',
        valueName: '',
      }
      this.dimensionVisible = true
    },
    handleEditDimension(item) {
      this.dimensionForm = {
        id: item.id,
        code: item.code,
        name: item.name,
        itemType: item.item_type,
        itemTypeName: item.item_type_name,
        valueCode: item.value_code,
        valueName: item.value_name,
      }
      this.dimensionVisible = true
    },
    handleDimensionOk() {
      this.$refs.dimensionForm.validate((valid) => {
        if (valid) {
          this.confirmLoading = true
          http({
            url: api.createOrUpdateAccountDimension,
            type: 'post',
            data: this.dimensionForm,
            success: (res) => {
              this.confirmLoading = false
              if (res.success) {
                this.$message.success(this.$t('操作成功'))
                this.dimensionVisible = false
                this.getDimensionList()
              }
            },
            fail: () => {
              this.confirmLoading = false
            },
          })
        }
      })
    },

    // Item Management
    handleAddDimensionItem() {
      this.itemForm = {
        id: undefined,
        name: undefined,
        code: undefined,
        business_unit_name: undefined,
        business_unit_code: undefined,
      }
      this.itemVisible = true
    },
    handleEditDimensionItem(record) {
      this.itemForm = {
        id: record.id,
        name: record.name,
        code: record.code,
        business_unit_name: record.business_unit_name,
        business_unit_code: record.business_unit_code,
      }
      this.itemVisible = true
    },
    handleItemOk() {
      this.$refs.itemForm.validate((valid) => {
        if (valid) {
          this.confirmLoading = true
          http({
            url: api.createOrUpdateAccountDimensionItem,
            type: 'post',
            data: {
              ...this.itemForm,
              type: this.selectedDimension.item_type,
              typeName: this.selectedDimension.item_type_name,
            },
            success: (res) => {
              this.confirmLoading = false
              if (res.success) {
                this.$message.success(this.$t('操作成功'))
                this.itemVisible = false
                this.getItemList()
              }
            },
            fail: () => {
              this.confirmLoading = false
            },
          })
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.left-content {
  width: 280px;
  border-right: 1px solid #e8e8e8;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;

  .left-header {
    padding: 16px;
    font-size: 16px;
    font-weight: bold;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .add-icon {
      cursor: pointer;
      color: #1890ff;
      &:hover {
        color: #40a9ff;
      }
    }
  }

  .dimension-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;

    .dimension-item {
      padding: 12px 16px;
      cursor: pointer;
      transition: all 0.3s;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-left: 3px solid transparent;

      &:hover {
        background-color: #f5f5f5;
        .edit-icon {
          opacity: 1;
        }
      }

      &.active {
        background-color: #e6f7ff;
        color: #1890ff;
        border-left-color: #1890ff;
      }

      .item-info {
        flex: 1;
        overflow: hidden;
        .item-name {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .item-code {
          font-size: 12px;
          color: #999;
          margin-top: 2px;
        }
      }

      .edit-icon {
        opacity: 0;
        margin-left: 8px;
        color: #1890ff;
        &:hover {
          color: #40a9ff;
        }
      }
    }
  }
}

.right-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 20px;

  .selected-title {
    font-size: 18px;
    font-weight: bold;
    color: #333;
    line-height: 48px;
  }
}
</style>
